/**
 * pushNotifications — client-side helpers for the Web Push opt-in flow.
 *
 *   1. Ensures /service-worker.js is registered. CRA's dev server skips
 *      SW registration globally, so we do it lazily on first opt-in here.
 *   2. Fetches the backend's VAPID public key.
 *   3. Calls PushManager.subscribe() with the right ApplicationServerKey.
 *   4. POSTs the resulting subscription JSON to /api/push/subscribe so the
 *      backend can target this device when a breach fires.
 *
 * The "unsubscribe" path mirrors all four steps in reverse so a user can
 * cleanly opt back out from the Settings panel.
 */
import api from "@/lib/api";

const SW_URL = "/service-worker.js";


export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function notificationPermission() {
  return typeof Notification === "undefined" ? "default" : Notification.permission;
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/** Decode a base64url string into the Uint8Array form Push wants. */
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const std = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(std);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function ensureSwRegistered() {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

/** True if there's an active subscription for this browser. */
export async function getActiveSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Request permission → subscribe → register with backend. Returns the
 *  PushSubscription on success. Throws a friendly Error on failure. */
export async function enablePush(siteId = "") {
  if (!pushSupported()) {
    throw new Error("This browser does not support push notifications.");
  }
  // 1. Ask the user — must be a gesture handler on iOS Safari.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    if (perm === "denied") {
      throw new Error("Notifications are blocked. Enable them in browser settings.");
    }
    throw new Error("Notification permission was not granted.");
  }

  // 2. Register the SW if it isn't already.
  const reg = await ensureSwRegistered();
  await navigator.serviceWorker.ready;

  // 3. Pull the VAPID public key fresh — operators may have rotated it.
  const { data } = await api.get("/push/public-key");
  if (!data?.publicKey) throw new Error("Server did not return a VAPID key.");

  // 4. Subscribe — reuse any existing subscription so we don't keep minting
  //    new endpoints if the user re-clicks.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  // 5. Hand the subscription to the backend.
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    site_id: siteId || "",
  });
  return sub;
}

/** Unsubscribe locally AND tell the backend to forget this endpoint. */
export async function disablePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* browser already cleared it */
  }
  try {
    await api.delete("/push/unsubscribe", { params: { endpoint } });
  } catch {
    /* already gone server-side */
  }
}

/** Send a "hello" notification to all of this user's devices. */
export async function sendTestNotification() {
  const { data } = await api.post("/push/test", {});
  return data;
}
